import React from 'react'
import { RealEstateCategory, RealEstateFeatures, SegmentationType, TransactionType } from '../../../../../@types/types'
import { Flow, FlowName, FlowScreenElement } from '../../../../../@types/flow-types'
import { PROPERTY_OWNER_OFFICE_SELL_FLOW_PATH } from '../../../../../services/routes/routes'
import { propertyOwnerOfficeSellSchema } from './propertyOwnerOfficeSellSchema'
import {
  SCREEN_ADDRESS,
  SCREEN_BASIC_INFO,
  SCREEN_CONNECTIONS,
  SCREEN_CONSTRUCTION,
  SCREEN_CONTACT_INFO,
  SCREEN_DESCRIPTION,
  SCREEN_MULTIMEDIA,
  SCREEN_OTHER_FACILITIES,
  SCREEN_REALTOR_LEAD_ENGINE,
  SCREENS,
} from '../../../../../services/domain/wizardScreens'
import ContactInfo from '../../../../../components/pages/insertionPage/screens/contactInfo/ContactInfo'
import Address from '../../../../../components/pages/insertionPage/screens/address/Address'
import Construction from '../../../../../components/pages/insertionPage/screens/construction/Construction'
import Multimedia from '../../../../../components/pages/insertionPage/screens/multimediaNew/Multimedia'

import { MultimediaUploaderType } from '../../../../../@types/multimedia-types'
import Description from '../../../../../components/pages/insertionPage/screens/description/Description'
import OtherFacilities from '../../../../../components/pages/insertionPage/screens/otherFacilities/OtherFacilities'
import {
  airConditioningField,
  estateFeatures,
  flooringType,
  freeFrom,
  interiorQuality,
  lanCablesField,
  parkingSpace,
  totalFloorsField,
} from '../../../../../components/pages/insertionPage/screens/otherFacilities/wrapper/otherFacilitiesFieldsWrapper'
import { getDefaultClasses } from '../../../../../utils/style-utils'
import {
  buildingType,
  mainFloorSpaceField,
  priceField,
  purchasePriceOnRequestToggle,
  separableSpaceField,
} from '../../../../../components/pages/insertionPage/screens/basicInfo/wrappers/basicInfoFieldsWrapper'
import { PriceFieldType } from '../../../../../components/pages/insertionPage/screens/basicInfo/fields/priceField/PriceField'
import BasicInfo from '../../../../../components/pages/insertionPage/screens/basicInfo/BasicInfo'
import { MainFloorType } from '../../../../../components/pages/insertionPage/screens/basicInfo/fields/mainFloorSpaceField/MainFloorSpaceField'
import { SeparableSpaceType } from '../../../../../components/pages/insertionPage/screens/basicInfo/fields/separableSpaceField/SeparableSpaceField'
import { BuildingTypeOption } from '../../../../../@types/dropdown-types'
import { priceToApiSell } from '../../../../../api/realEstate/converters/toApi/estate/price/priceToApi'
import Connections from '../../../../../components/pages/insertionPage/screens/connections/Connections'
import { RealtorLeadEngine } from '../../../../../components/pages/insertionPage/screens/realtorLeadEngine/RealtorLeadEngine'

const propertyOwnerOfficeSellFlowScreens: FlowScreenElement[] = [
  {
    screen: SCREENS[SCREEN_ADDRESS],
    element: (props) => <Address {...props} />,
  },
  {
    screen: SCREENS[SCREEN_BASIC_INFO],
    element: (props) => (
      <BasicInfo
        {...props}
        screenTooltipKey='tooltip.screens.basicInfo.officeRentSell'
        properties={[
          buildingType([
            BuildingTypeOption.LOFT,
            BuildingTypeOption.STUDIO,
            BuildingTypeOption.OFFICE,
            BuildingTypeOption.OFFICE_FLOOR,
            BuildingTypeOption.OFFICE_BUILDING,
            BuildingTypeOption.OFFICE_CENTRE,
            BuildingTypeOption.OFFICE_STORAGE_BUILDING,
            BuildingTypeOption.SURGERY,
            BuildingTypeOption.SURGERY_FLOOR,
            BuildingTypeOption.SURGERY_BUILDING,
            BuildingTypeOption.COMMERCIAL_CENTRE,
            BuildingTypeOption.LIVING_AND_COMMERCIAL_BUILDING,
            BuildingTypeOption.OFFICE_AND_COMMERCIAL_BUILDING,
          ]),
          mainFloorSpaceField(MainFloorType.AREA, 'left'),
          separableSpaceField(SeparableSpaceType.AREA, 'right'),
          priceField(PriceFieldType.BUY_PRICE, getDefaultClasses('left')),
          freeFrom(getDefaultClasses('right')),
          purchasePriceOnRequestToggle(),
        ]}
      />
    ),
  },
  {
    screen: SCREENS[SCREEN_DESCRIPTION],
    element: (props) => <Description {...props} hasFurnishingNote={true} hasLocationNote={true} hasOtherNote={true} />,
  },
  {
    screen: SCREENS[SCREEN_MULTIMEDIA],
    element: (props) => (
      <Multimedia
        {...props}
        doCloseMultimediaStatusMessage={props.doCloseMultimediaStatusMessage!}
        allowedTypesToDisplay={[
          MultimediaUploaderType.PICTURE,
          MultimediaUploaderType.FLOOR_PLAN,
          MultimediaUploaderType.DOCUMENT,
          MultimediaUploaderType.VIDEO,
        ]}
      />
    ),
  },
  {
    screen: SCREENS[SCREEN_OTHER_FACILITIES],
    element: (props) => (
      <OtherFacilities
        {...props}
        screenTooltipKey='tooltip.screens.otherFacilities.allSell'
        properties={[
          estateFeatures([
            RealEstateFeatures.BASEMENT,
            RealEstateFeatures.HAS_CANTEEN,
            RealEstateFeatures.HANDICAPPED_ACCESSIBLE,
            RealEstateFeatures.HIGH_VOLTAGE,
            RealEstateFeatures.ELEVATOR,
            RealEstateFeatures.KITCHEN_COMPLETE,
          ]),
          interiorQuality(),
          lanCablesField(),
          airConditioningField(),
          flooringType(),
          totalFloorsField(getDefaultClasses('full')),
          parkingSpace({ showParkingSpaceType: false, parkingSpaceRentTypeLabel: 'RentPerItem' }),
        ]}
      />
    ),
  },
  {
    screen: SCREENS[SCREEN_CONNECTIONS],
    element: (props) => <Connections {...props} />,
  },
  {
    screen: SCREENS[SCREEN_CONSTRUCTION],
    element: (props) => <Construction {...props} hasLastModernizationField={true} />,
  },
  {
    screen: SCREENS[SCREEN_REALTOR_LEAD_ENGINE],
    element: (props) => <RealtorLeadEngine {...props} />,
  },
  {
    screen: SCREENS[SCREEN_CONTACT_INFO],
    element: (props) => <ContactInfo {...props} />,
  },
]

export const propertyOwnerOfficeSellFlow: Flow = {
  flowInformation: {
    owner: 'wizard.navigation.header.owner',
    estateType: 'wizard.navigation.header.office',
    offerType: 'wizard.navigation.header.sells',
    path: PROPERTY_OWNER_OFFICE_SELL_FLOW_PATH,
    name: FlowName.PROPERTY_OWNER_OFFICE_SELL,
    validationSchema: propertyOwnerOfficeSellSchema,
    priceToApiMapper: priceToApiSell,
    metaData: {
      category: RealEstateCategory.OFFICE,
      segmentation: SegmentationType.PROPERTY_OWNER,
      transaction: TransactionType.SELL,
    },
  },
  defaultStateValues: { hideStreetAndStreetNumber: false },
  flowScreens: propertyOwnerOfficeSellFlowScreens,
}
